import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import type { SharedMarketingProps } from '@/types/marketing'

/**
 * Pieces shared by the three sign-in pages (/log-in, /account-hub/log-in,
 * /verify-otp). Every form here is a PLAIN HTML post — not an Inertia visit —
 * to the same endpoints, with the same params, as the ERB pages these replaced:
 * a successful sign-in lands on the portal, a different Inertia app, and must
 * be a full page load that picks up the new session cookie. Rack::Attack
 * throttles those paths, so the paths are the contract.
 */

export interface SignInPaths {
  login: string
  verify: string
  resend: string
  apply: string
}

export interface SignInPageProps extends SharedMarketingProps {
  /** Rails' forgery-protection field, as a prop so the server-rendered form is
   *  complete before any JavaScript runs. */
  csrf: { param: string; token: string }
  flash: { notice?: string; alert?: string }
  return_to: string | null
  /** True in the Account Hub-only flow — errors and back-links stay in it. */
  hub: boolean
  paths: SignInPaths
  help_email: string
}

/** The fields every sign-in form posts besides its own. */
export function HiddenFields({ csrf, returnTo, hub }: { csrf: SignInPageProps['csrf']; returnTo: string | null; hub: boolean }) {
  return (
    <>
      <input type="hidden" name={csrf.param} value={csrf.token} />
      {returnTo && <input type="hidden" name="return_to" value={returnTo} />}
      {hub && <input type="hidden" name="hub" value="1" />}
    </>
  )
}

/** The alert's element id: the field the alert is about points at it. */
export const SIGN_IN_ALERT_ID = 'signin-alert'

/**
 * aria-invalid + aria-describedby for the field a server alert is about, so a
 * screen reader announces the alert with the field, not only once on load.
 * `hint` is the field's own description, kept alongside.
 */
export function alertFieldProps(alert: string | undefined, hint?: string) {
  const describedBy = [alert ? SIGN_IN_ALERT_ID : null, hint ?? null].filter(Boolean).join(' ')
  return {
    'aria-invalid': alert ? (true as const) : undefined,
    'aria-describedby': describedBy || undefined,
  }
}

/**
 * The server's notice and alert for these pages. The MARKETING layout reports
 * no flash of its own (unlike the console layouts), so this is the only place
 * it is drawn — the sign-in pages are the marketing pages that have one.
 */
export function FlashMessages({ alert, notice }: SignInPageProps['flash']) {
  if (!alert && !notice) return null
  return (
    <div className="space-y-3">
      {alert && (
        <p id={SIGN_IN_ALERT_ID} role="alert" className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {alert}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
          {notice}
        </p>
      )}
    </div>
  )
}

/**
 * True from the moment a form submits until the page is replaced, so a second
 * click cannot send a second code. Reset when the browser restores the page
 * from its back/forward cache — otherwise Back leaves a dead button.
 */
export function useBusy(): [boolean, (busy: boolean) => void] {
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => { if (event.persisted) setBusy(false) }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])
  return [busy, setBusy]
}

export function SubmitButton({ busy, idle, working }: { busy: boolean; idle: string; working: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      aria-busy={busy || undefined}
      className="marketing-button-primary w-full justify-center disabled:cursor-wait disabled:opacity-70"
    >
      {busy ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span aria-live="polite">{working}</span>
        </>
      ) : (
        <>
          {idle}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </>
      )}
    </button>
  )
}

/** The Account Hub's email form — on /log-in and on /account-hub/log-in. */
export function EmailForm({ props, autoFocus = false }: { props: SignInPageProps; autoFocus?: boolean }) {
  const [busy, setBusy] = useBusy()
  return (
    <form method="post" action={props.paths.login} className="space-y-4" onSubmit={() => setBusy(true)}>
      <HiddenFields csrf={props.csrf} returnTo={props.return_to} hub={props.hub} />
      <div>
        <label htmlFor="email" className="apply-label">Email address</label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus={autoFocus}
          placeholder="you@example.com"
          {...alertFieldProps(props.flash.alert)}
          className="apply-input"
        />
      </div>
      <SubmitButton busy={busy} idle="Send login code" working="Sending code…" />
    </form>
  )
}

export function HelpLine({ props, stacked = false }: { props: SignInPageProps; stacked?: boolean }) {
  // Stacked: one line each, for a column too narrow to hold both side by side
  // without breaking a phrase in two.
  return (
    <div className={stacked
      ? 'flex flex-col gap-2 text-sm leading-6 text-white/60'
      : 'flex flex-col items-center justify-center gap-3 text-sm text-white/60 sm:flex-row sm:gap-6'}>
      <p>
        Haven&apos;t applied yet?{' '}
        <a href={props.paths.apply} className="whitespace-nowrap font-medium text-[#ff8c4d] underline-offset-4 hover:underline">Apply for an account</a>
      </p>
      {!stacked && <span className="hidden text-white/20 sm:inline" aria-hidden="true">·</span>}
      <p>
        Need help?{' '}
        <a href={`mailto:${props.help_email}`} className="whitespace-nowrap font-medium text-[#ff8c4d] underline-offset-4 hover:underline">{props.help_email}</a>
      </p>
    </div>
  )
}

/**
 * For a card that holds a FORM. .marketing-surface-card lifts 4px on hover —
 * right for a card you click, wrong for one you type in: it moves out from
 * under the pointer and the hover flickers on and off. Inline beats :hover.
 */
export const STILL_CARD = { transform: 'none', transition: 'none', boxShadow: '0 24px 56px rgba(0, 0, 0, 0.42)' } as const

/** The narrow single-card column the hub login and the code page share. */
export function SignInCard({ eyebrow, title, lead, children }: { eyebrow: string; title: ReactNode; lead: ReactNode; children: ReactNode }) {
  return (
    <div className="marketing-surface-card" style={STILL_CARD}>
      <span className="marketing-eyebrow">{eyebrow}</span>
      <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">{title}</h1>
      <p className="mt-3 text-sm leading-7 text-white/70">{lead}</p>
      <div className="mt-8 space-y-6">{children}</div>
    </div>
  )
}
